/** Formatting + small pure helpers for the Policy Admin System. */

import type { QuoteOutput } from '../Quotes/types';
import type { QuoteCostBreakdown } from './types';

export function formatCurrency(value: number, fractionDigits = 0): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

export function formatPercent(value: number, fractionDigits = 0): string {
  return `${value.toFixed(fractionDigits)}%`;
}

/** YYYY-MM-DD (or ISO) -> "Mar 25, 2026". Falls back to the raw string. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function relativeTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = date.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  const abs = Math.abs(diffMin);
  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
  if (abs < 60) return rtf.format(diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hour');
  const diffDay = Math.round(diffHr / 24);
  if (Math.abs(diffDay) < 30) return rtf.format(diffDay, 'day');
  const diffMonth = Math.round(diffDay / 30);
  return rtf.format(diffMonth, 'month');
}

export function nowIso(): string {
  return new Date().toISOString();
}

/** YYYY-MM-DD for a Date offset by a number of months from `base`. */
export function addMonths(dateStr: string, months: number): string {
  const date = new Date(`${dateStr}T00:00:00`);
  date.setMonth(date.getMonth() + months);
  return date.toISOString().slice(0, 10);
}

export function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export function daysBetween(fromStr: string, toStr: string): number {
  const from = new Date(`${fromStr}T00:00:00`).getTime();
  const to = new Date(`${toStr}T00:00:00`).getTime();
  return Math.round((to - from) / 86_400_000);
}

let idCounter = 0;
export function makeId(prefix: string): string {
  idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}${idCounter.toString(36)}${Math.floor(
    Math.random() * 1e6,
  ).toString(36)}`;
}

export function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export type SortDirection = 'asc' | 'desc';

/** Stable comparator over a resolved key; strings compare case-insensitively. */
export function compareBy<T>(
  a: T,
  b: T,
  getKey: (item: T) => string | number,
  direction: SortDirection,
): number {
  const av = getKey(a);
  const bv = getKey(b);
  let result: number;
  if (typeof av === 'number' && typeof bv === 'number') {
    result = av - bv;
  } else {
    result = String(av).localeCompare(String(bv), 'en-US', { sensitivity: 'base' });
  }
  return direction === 'asc' ? result : -result;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Demo-only: fabricate a downloadable text stand-in for a PDF so the download
 * actions in the UI produce a real file without a backend.
 */
export function triggerMockDownload(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.replace(/\.pdf$/i, '.txt');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Download a document via its presigned URL. The backend signs the
 * `Content-Disposition` header, so a plain navigation triggers the download
 * with the correct filename.
 */
export function triggerUrlDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Parse a quote-output amount; blank or unparseable values are unknown (null). */
function parseAmount(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * The "Total Costs" block of a quote output, in the shape the PAS create-from-quote
 * endpoint expects. Mirrors the backend's `serialize_approved_quote` so a policy
 * created from the Quote detail page carries the same breakdown as one created
 * from the approved-quotes picker; the itemised amounts feed the policy schedule.
 */
export function costsFromQuoteOutput(output: QuoteOutput): QuoteCostBreakdown {
  const sasFee = parseAmount(output.quoted_sas_fee);
  const lmsFee = parseAmount(output.quoted_lms_fee);
  return {
    totalProgramCostWithoutFees:
      parseAmount(output.total_program_cost_without_tax_and_stamping_fee) ?? 0,
    taxStampingIssuanceFees: parseAmount(output.tax_and_stamping_fee) ?? 0,
    totalProgramCostWithFees: parseAmount(output.total_program_costs_with_tax_stamping_fee) ?? 0,
    quotedPremium: parseAmount(output.quoted_premium),
    surplusLinesTax: parseAmount(output.surplus_lines_tax),
    stampingFee: parseAmount(output.stamping_fee),
    policyIssuanceFee: parseAmount(output.policy_issuance_fee),
    otherFees: sasFee === null && lmsFee === null ? null : (sasFee ?? 0) + (lmsFee ?? 0),
  };
}
